import React, { useState, useEffect, useRef, useCallback } from 'react';

const Resources = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [showTooltip, setShowTooltip] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const intervalRef = useRef(null);
  const touchStartX = useRef(null);
  const dragStartX = useRef(null);

  const resources = [
    {
      id: 1,
      name: "Arihant's CLASS 12 Chemistry Study Companion",
      link: "https://amzn.to/4avSdE2",
      image: "/images/products/Arihant-Chemistry-12.png",
    },
    {
      id: 2,
      name: "Oswaal Class 11 Chemistry Question Bank",
      link: "https://amzn.to/4o90S4W",
      image: "/images/products/Oswaal_QB_11.png",
    },
    {
      id: 3,
      name: "HC Verma Concepts of Physics (2026-27)",
      link: "https://amzn.to/4uiACGK",
      image: "/images/products/HC_Verma_Physics.png",
    },

  ];

  const tooltipPoints = [
    "Items featured here are independently available third-party products.",
    "Any materials or resources formally intended to be provided by the tutor as part of the course curriculum are entirely separate from - and should not be confused with - what appears in this section.",
    "These are optional add-ons you may explore at your own interest.",
  ];

  const sans = { fontFamily: "'Helvetica Neue', Arial, sans-serif" };
  const n = resources.length;

  const startInterval = useCallback(() => {
  clearInterval(intervalRef.current);

  intervalRef.current = setInterval(() => {
    setActiveIndex(prev => (prev + 1) % n);
  }, 4000);
}, [n]);

useEffect(() => {
  if (!isHovered) {
    startInterval();
  } else {
    clearInterval(intervalRef.current);
  }

  return () => clearInterval(intervalRef.current);
}, [isHovered, startInterval]);

  const goTo = i => {
    clearInterval(intervalRef.current);
    setActiveIndex(((i % n) + n) % n);
    startInterval();
  };

  const onTouchStart = e => {
    touchStartX.current = e.touches[0].clientX;
    dragStartX.current = e.touches[0].clientX;
    setIsDragging(true);
    clearInterval(intervalRef.current);
  };

  const onTouchMove = e => {
    if (dragStartX.current === null) return;
    setDragOffset(e.touches[0].clientX - dragStartX.current);
  };

  const onTouchEnd = e => {
    const delta = touchStartX.current - e.changedTouches[0].clientX;
    setDragOffset(0);
    setIsDragging(false);
    if (Math.abs(delta) > 40) {
      setActiveIndex(prev => delta > 0 ? (prev + 1) % n : (prev - 1 + n) % n);
    }
    startInterval();
    touchStartX.current = null;
    dragStartX.current = null;
  };

  return (
    <section
      style={{ padding: '28px 16px', borderTop: '1px solid #e5e7eb', background: '#fff', ...sans }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <style>{`
        .res-arrow {
          transition: background 0.18s ease, transform 0.15s ease;
        }
        .res-arrow:hover {
          background: #111 !important;
          transform: scale(1.08);
        }
        .res-arrow:hover svg polyline {
          stroke: #fff !important;
        }
        .res-arrow:active {
          transform: scale(0.95);
        }

        .res-vd {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          font-size: 12.5px;
          font-weight: 500;
          color: #fff !important;
          background: #111;
          text-decoration: none;
          border-radius: 5px;
          padding: 9px 20px;
          letter-spacing: 0.02em;
          transition: opacity 0.18s ease;
          -webkit-tap-highlight-color: transparent;
        }
        .res-vd:hover { opacity: 0.8; }
        .res-vd:active { opacity: 0.6; }

        .info-btn {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          border: 1.5px solid #555;
          background: transparent;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          flex-shrink: 0;
          transition: background 0.15s ease, border-color 0.15s ease;
          -webkit-tap-highlight-color: transparent;
          outline: none;
        }
        .info-btn:hover,
        .info-btn:focus,
        .info-btn.filled {
          background: #111 !important;
          border-color: #111 !important;
        }
        .info-btn:hover .info-icon,
        .info-btn:focus .info-icon,
        .info-btn.filled .info-icon {
          stroke: #fff !important;
        }
        .info-icon {
          stroke: #555;
          transition: stroke 0.15s ease;
        }

        .res-img-panel {
          width: 200px;
          flex-shrink: 0;
          background: #f7f7f7;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px 18px;
          min-height: 240px;
          border-right: 1px solid #f0f0f0;
        }
        .res-img {
          width: 100%;
          max-width: 165px;
          height: 200px;
          object-fit: contain;
          display: block;
        }

        @media (max-width: 500px) {
          .res-card-inner {
            flex-direction: column !important;
            align-items: center !important;
          }
          .res-img-panel {
            width: 100% !important;
            min-height: 0 !important;
            padding: 20px 16px 16px !important;
            border-bottom: 1px solid #f0f0f0 !important;
            border-right: none !important;
          }
          .res-img {
            height: 150px !important;
            max-width: 120px !important;
          }
          .res-info-panel {
            padding: 18px 16px 22px !important;
          }
        }
      `}</style>

      <div style={{ maxWidth: '800px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
          <span style={{ fontSize: '13.5px', fontWeight: 600, color: '#111', letterSpacing: '-0.01em' }}>
            Check out more academic essentials!
          </span>

          <div style={{ position: 'relative', display: 'inline-flex' }}>
            <button
              className={`info-btn${showTooltip ? ' filled' : ''}`}
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
              onFocus={() => setShowTooltip(true)}
              onBlur={() => setShowTooltip(false)}
              onTouchStart={e => { e.preventDefault(); setShowTooltip(v => !v); }}
              aria-label="About these resources"
            >
              <svg
                width="9" height="9" viewBox="0 0 24 24"
                fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                className="info-icon"
              >
                <line x1="12" y1="17" x2="12" y2="11" />
                <line x1="12" y1="7" x2="12.01" y2="7" />
              </svg>
            </button>

            {showTooltip && (
              <div style={{
                position: 'absolute', top: '28px', left: '50%',
                transform: 'translateX(-50%)', background: '#111', color: '#fff',
                fontSize: '11px', lineHeight: '1.65', padding: '12px 14px',
                borderRadius: '7px', width: '268px', zIndex: 200, ...sans
              }}>
                {/* Caret */}
                <span style={{
                  position: 'absolute', top: '-5px', left: '50%',
                  transform: 'translateX(-50%)', width: 0, height: 0,
                  borderLeft: '5px solid transparent', borderRight: '5px solid transparent',
                  borderBottom: '5px solid #111',
                }} />

                {/* Points */}
                <ol style={{ margin: 0, padding: '0 0 0 14px', listStyleType: 'decimal', textAlign: 'justify' }}>
                  {tooltipPoints.map((point, i) => (
                    <li
                      key={i}
                      style={{
                        marginBottom: i < tooltipPoints.length - 1 ? '9px' : 0,
                        paddingLeft: '2px',
                        color: '#fff',
                      }}
                    >
                      {point}
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        </div>

        {/* Carousel row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>

          {/* Left arrow */}
          <button
            className="res-arrow"
            onClick={() => goTo(activeIndex - 1)}
            aria-label="Previous"
            style={{
              flexShrink: 0, width: '38px', height: '38px', borderRadius: '50%',
              border: '1.5px solid #111', background: '#fff', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: 0, zIndex: 1,
            }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
              strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" stroke="#111" />
            </svg>
          </button>

          {/* Card */}
          <div style={{
            flex: 1, position: 'relative', overflow: 'hidden',
            borderRadius: '7px', border: '1px solid #e5e7eb',
          }}>
            <div
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
              style={{
                display: 'flex',
                transform: `translateX(calc(${-activeIndex * 100}% + ${isDragging ? dragOffset : 0}px))`,
                transition: isDragging ? 'none' : 'transform 0.35s cubic-bezier(0.4,0,0.2,1)',
                willChange: 'transform',
                touchAction: 'pan-y',
              }}
            >
              {resources.map(item => (
                <div key={item.id} style={{ minWidth: '100%' }}>
                  <div
                    className="res-card-inner"
                    style={{ display: 'flex', background: '#fff' }}
                  >
                    {/* Image panel */}
                    <div className="res-img-panel">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="res-img"
                        onError={e => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div style={{
                        display: 'none', alignItems: 'center', justifyContent: 'center',
                        width: '100%', height: '160px', background: '#ececec', borderRadius: '4px',
                      }}>
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
                          stroke="#aaa" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                        </svg>
                      </div>
                    </div>

                    {/* Info panel */}
                    <div
                      className="res-info-panel"
                      style={{
                        flex: 1,
                        padding: '24px 20px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '18px',
                        textAlign: 'center',
                        background: '#fff',
                      }}
                    >
                      <p style={{
                        margin: 0,
                        fontSize: 'clamp(13px, 2vw, 14.5px)',
                        fontWeight: 600,
                        color: '#111',
                        lineHeight: 1.45,
                        ...sans,
                      }}>
                        {item.name}
                      </p>

                      <a
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="res-vd"
                      >
                        View details
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
                          stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                          <polyline points="15 3 21 3 21 9" />
                          <line x1="10" y1="14" x2="21" y2="3" />
                        </svg>
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right arrow */}
          <button
            className="res-arrow"
            onClick={() => goTo(activeIndex + 1)}
            aria-label="Next"
            style={{
              flexShrink: 0, width: '38px', height: '38px', borderRadius: '50%',
              border: '1.5px solid #111', background: '#fff', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: 0, zIndex: 1,
            }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
              strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" stroke="#111" />
            </svg>
          </button>
        </div>

        {/* Dots */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', marginTop: '14px' }}>
          {resources.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              aria-label={`Go to item ${i + 1}`}
              style={{
                width: i === activeIndex ? '22px' : '7px',
                height: '7px',
                borderRadius: '4px',
                background: i === activeIndex ? '#111' : '#d1d5db',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                transition: 'width 0.3s ease, background 0.3s ease',
              }}
            />
          ))}
        </div>

      </div>
    </section>
  );
};

export default Resources;